import {PageHeader} from '../ui/PageHeader';
import { DateOnlyInput } from '../components/DateOnlyInput';
import { alphaSave } from '../ui/alpha-save';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { api } from '../api';
import { ErrorState, Loading } from '../ui/State';
type Pack = {
  id: string;
  report_kind: string;
  status: string;
  version: number;
  period_start: string;
  period_end: string;
  commentary: string;
  snapshot: {
    building: { name: string };
    coverage: string;
    work: { status: string; count: number }[];
    governance: { status: string; count: number }[];
    compliance: { status: string; count: number }[];
    finance: { id: string; name: string; debits: string; credits: string; postings: number }[];
  };
};
export function BoardPacksAlpha({ portal = false }: { portal?: boolean }) {
  const [q] = useSearchParams(),
    building = q.get('building'),
    [items, setItems] = useState<Pack[]>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false);
  const pending = useRef<{ url: string; body: object } | null>(null);
  const endpoint = portal
    ? `/api/portal/board-packs/${building}`
    : `/api/alpha/board-packs?building=${building}`;
  const load = () => api<{ items: Pack[] }>(endpoint).then((r) => setItems(r.items));
  useEffect(() => {
    setItems(undefined);
    if (!building) return;
    let active = true;
    api<{ items: Pack[] }>(endpoint)
      .then((r) => {
        if (active) setItems(r.items);
      })
      .catch((e) => {
        if (active) setError(e);
      });
    return () => {
      active = false;
    };
  }, [endpoint]);
  async function write(url: string, body: object) {
    setBusy(true);
    setError(undefined);
    pending.current ??= { url, body: { ...body, idempotencyKey: crypto.randomUUID() } };
    try {
      await alphaSave(pending, pending.current.url, {
        method: 'POST',
        body: JSON.stringify(pending.current.body),
      });
      await load();
      pending.current = null;
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  function prepare(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    void write('/api/alpha/board-packs', {
      building,
      kind: f.get('kind'),
      periodStart: f.get('from'),
      periodEnd: f.get('to'),
      commentary: f.get('commentary'),
    });
  }
  return (
    <>
      <PageHeader title={portal ? 'Your board packs' : 'Board and AGM packs'}/>
      {Boolean(error) && <ErrorState error={error} />}{' '}
      {pending.current && !busy && (
        <button onClick={() => void write(pending.current!.url, {})}>Retry the same save</button>
      )}
      {!building ? (
        <p>Open board packs from a building.</p>
      ) : (
        <>
          {!portal && (
            <form className="panel form" onSubmit={prepare}>
              <fieldset disabled={busy || !!pending.current}>
                <legend>Prepare a new pack</legend>
                <label>
                  Pack type
                  <select name="kind">
                    <option>Board pack</option>
                    <option>AGM pack</option>
                  </select>
                </label>
                <label>
                  Period start
                  <DateOnlyInput name="from" required />
                </label>
                <label>
                  Period end
                  <DateOnlyInput name="to" required />
                </label>
                <label>
                  Manager’s commentary
                  <textarea name="commentary" required minLength={10} maxLength={10000} />
                </label>
                <button>Prepare draft</button>
              </fieldset>
            </form>
          )}
          {!items ? (
            <Loading />
          ) : items.length === 0 ? (
            <p>No packs available.</p>
          ) : (
            items.map((p) => (
              <section className="panel" key={p.id}>
                <h2>
                  {p.report_kind} · {p.snapshot.building.name}
                </h2>
                <p>
                  {p.period_start.slice(0, 10)} to {p.period_end.slice(0, 10)} ·{' '}
                  {p.status ?? 'Published'}
                </p>
                <p>{p.commentary}</p>
                <p>{p.snapshot.coverage}</p>
                {(['work', 'governance', 'compliance'] as const).map((section) => (
                  <section key={section}>
                    <h3>
                      {section === 'work'
                        ? 'Repairs and work'
                        : section === 'governance'
                          ? 'Governance'
                          : 'Compliance register'}
                    </h3>
                    {p.snapshot[section].length ? (
                      <ul>
                        {p.snapshot[section].map((r) => (
                          <li key={r.status}>
                            {r.status}: {r.count}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p>No source records available.</p>
                    )}
                  </section>
                ))}
                <h3>Posted financial movements</h3>
                {p.snapshot.finance.map((f) => (
                  <p key={f.id}>
                    {f.name}: debits £{f.debits}, credits £{f.credits} ({f.postings} postings)
                  </p>
                ))}
                {!portal && p.status === 'Draft' && (
                  <button
                    disabled={busy || !!pending.current}
                    onClick={() =>
                      void write(`/api/alpha/board-packs/${p.id}/approve`, {
                        expectedVersion: p.version,
                      })
                    }
                  >
                    Approve this pack
                  </button>
                )}
                {!portal && p.status === 'Approved' && (
                  <button
                    disabled={busy || !!pending.current}
                    onClick={() =>
                      void write(`/api/alpha/board-packs/${p.id}/publish`, {
                        expectedVersion: p.version,
                      })
                    }
                  >
                    Publish to directors
                  </button>
                )}
              </section>
            ))
          )}
        </>
      )}
    </>
  );
}
